// admin-ui/src/api/marketing-picks.ts
import { api } from "./client";

export type SlotType = "hero_trailer" | "issue_spread" | "sample_article" | "issue_preview";

export interface CurrentIssue {
  id: number;
  title: string;
  pages_base_url: string | null;
  pages_query: string | null;
  page_count: number | null;
}
export type TargetKind = "archive_article" | "digital_edition";

export interface TargetPreview {
  kind: TargetKind;
  id: number;
  title: string;
  subtitle: string | null;
  image_url: string | null;
  trailer_published: boolean | null;
  page_count: number | null;
  pages_base_url: string | null;   // editions only — drives the Edit-drawer preview
  pages_query?: string | null;     // signed CloudFront query for those pages
}

export interface PickRow {
  id: number;
  publication_slug: string;
  slot_type: SlotType;
  position: number;
  target_kind: TargetKind;
  target_id: number;
  caption: string | null;
  excerpt_override: string | null;
  meta: Record<string, unknown> | null;
  status: "active" | "draft" | "retired";
  target: TargetPreview | null;
}

export interface Candidate {
  kind: TargetKind;
  id: number;
  title: string;
  subtitle: string | null;
  image_url: string | null;
  trailer_published: boolean | null;
  page_count: number | null;
  pages_base_url: string | null;
  pages_query?: string | null;
}

export const marketingPicksApi = {
  list: (publication_slug: string, includeRetired = false) =>
    api.get<{ picks: PickRow[]; current_issue: CurrentIssue | null }>(
      `/api/admin/marketing-picks?publication_slug=${encodeURIComponent(publication_slug)}${
        includeRetired ? "&include_retired=true" : ""
      }`,
    ),

  candidates: (publication_slug: string, slot_type: SlotType, q?: string) => {
    const params = new URLSearchParams({ publication_slug, slot_type });
    if (q) params.set("q", q);
    return api.get<{ candidates: Candidate[] }>(
      `/api/admin/marketing-picks/candidates?${params}`,
    );
  },

  create: (body: {
    publication_slug: string;
    slot_type: SlotType;
    target_kind: TargetKind;
    target_id: number;
    caption?: string | null;
    excerpt_override?: string | null;
    meta?: Record<string, unknown> | null;
  }) => api.post<PickRow>("/api/admin/marketing-picks", body),

  patch: (id: number, body: {
    caption?: string | null;
    excerpt_override?: string | null;
    status?: "active" | "draft" | "retired";
    meta?: Record<string, unknown> | null;
    target_id?: number;   // repoint at a different article/edition in place
  }) => api.patch<PickRow>(`/api/admin/marketing-picks/${id}`, body),

  delete: (id: number) => api.del<void>(`/api/admin/marketing-picks/${id}`),

  reorder: (publication_slug: string, slot_type: SlotType, ids_in_order: number[]) =>
    api.post<{ picks: PickRow[] }>("/api/admin/marketing-picks/reorder", {
      publication_slug,
      slot_type,
      ids_in_order,
    }),
};
